import { useEffect, useState } from "react";
import { Loader2, Plus, Users, Trash2, UserPlus, Calendar, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * CohortsManager — admin UI to manage cohorts for a single course product.
 * Cohorts define a start_date that anchors the drip-unlock schedule.
 */
export default function CohortsManager({ productId }) {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", capacity: "" });
  const [expanded, setExpanded] = useState(null); // cohort_id
  const [enrollEmail, setEnrollEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lms/${productId}/cohorts`);
      setCohorts(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date) return;
    setCreating(true);
    try {
      await api.post(`/admin/lms/${productId}/cohorts`, {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date || null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
      });
      setForm({ name: "", start_date: "", end_date: "", capacity: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete cohort and all its enrollments?")) return;
    try {
      await api.delete(`/admin/lms/cohorts/${id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const enroll = async (cohortId) => {
    if (!enrollEmail.trim()) return;
    try {
      await api.post(`/admin/lms/cohorts/${cohortId}/enroll`, { user_email: enrollEmail.trim().toLowerCase() });
      setEnrollEmail("");
      setExpanded(null);
      setTimeout(() => setExpanded(cohortId), 50); // reload members
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border-t border-[var(--line)] p-5 space-y-4" data-testid="cohorts-manager">
      <div className="flex items-center gap-2">
        <Users size={14} className="text-[var(--gold)]" />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--navy)]">Cohorts</span>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2">{error}</div>}

      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <div className="col-span-2">
          <label className="block text-[10px] tracking-widest text-[var(--muted)] uppercase mb-1">Cohort name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Spring 2026"
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
            data-testid="cohort-name"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest text-[var(--muted)] uppercase mb-1">Start date</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
            data-testid="cohort-start"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest text-[var(--muted)] uppercase mb-1">End date</label>
          <input
            type="date"
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={creating || !form.name.trim() || !form.start_date}
          className="btn btn-navy !py-2 !px-3 !text-[11px] disabled:opacity-60"
          data-testid="cohort-create"
        >
          {creating ? <Loader2 className="animate-spin" size={12} /> : <><Plus size={12} className="mr-1" /> Create</>}
        </button>
      </form>

      {loading ? (
        <div className="text-xs text-[var(--muted)]">Loading cohorts…</div>
      ) : cohorts.length === 0 ? (
        <div className="text-xs text-[var(--muted)] py-3">No cohorts yet. Create one above to gate lesson unlocks by cohort start date.</div>
      ) : (
        <ul className="space-y-2">
          {cohorts.map((c) => (
            <li key={c.id} className="border border-[var(--line)] bg-[var(--cream)]" data-testid={`cohort-${c.id}`}>
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[var(--navy)] text-sm">{c.name}</div>
                  <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider flex items-center gap-2">
                    <Calendar size={10} /> Starts {new Date(c.start_date).toLocaleDateString()}
                    {c.end_date && <> · Ends {new Date(c.end_date).toLocaleDateString()}</>}
                    <> · {c.enrolled_count} enrolled</>
                  </div>
                </div>
                <button
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  className="btn btn-ghost !py-1 !px-2 !text-[11px]"
                  data-testid={`cohort-manage-${c.id}`}
                >
                  {expanded === c.id ? <X size={12} /> : <UserPlus size={12} />}
                </button>
                <button
                  onClick={() => remove(c.id)}
                  className="p-1.5 hover:bg-red-50 text-red-700"
                  data-testid={`cohort-delete-${c.id}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
              {expanded === c.id && (
                <CohortMembers
                  cohortId={c.id}
                  onEnroll={enroll}
                  enrollEmail={enrollEmail}
                  setEnrollEmail={setEnrollEmail}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CohortMembers({ cohortId, onEnroll, enrollEmail, setEnrollEmail }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lms/cohorts/${cohortId}/members`);
      setMembers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cohortId]);

  const kick = async (email) => {
    if (!window.confirm(`Remove ${email} from this cohort?`)) return;
    await api.delete(`/admin/lms/cohorts/${cohortId}/members/${encodeURIComponent(email)}`);
    await load();
  };

  return (
    <div className="border-t border-[var(--line)] bg-white p-4 space-y-3">
      <div className="flex gap-2">
        <input
          type="email"
          value={enrollEmail}
          onChange={(e) => setEnrollEmail(e.target.value)}
          placeholder="learner@example.com"
          className="flex-1 px-3 py-2 border border-[var(--line)] text-sm"
          data-testid="cohort-enroll-email"
        />
        <button
          onClick={() => onEnroll(cohortId)}
          className="btn btn-navy !py-1.5 !px-3 !text-[11px]"
          data-testid="cohort-enroll-submit"
        >
          <UserPlus size={12} className="mr-1" /> Enroll
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-[var(--muted)]">Loading members…</div>
      ) : members.length === 0 ? (
        <div className="text-xs text-[var(--muted)] italic">No learners enrolled yet.</div>
      ) : (
        <ul className="space-y-1">
          {members.map((m) => (
            <li key={m.user_email} className="flex items-center gap-2 text-xs border border-[var(--line)] px-3 py-2 bg-[var(--cream)]">
              <span className="font-mono flex-1">{m.user_email}</span>
              <span className="text-[var(--muted)]">
                {m.completed_lessons}/{m.total_lessons} · {m.percent}%
              </span>
              <button
                onClick={() => kick(m.user_email)}
                className="p-1 hover:bg-red-50 text-red-700"
                title="Remove"
              >
                <Trash2 size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
